import { useCallback, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TransactionsTableRow } from './TransactionTableRow'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '@/utils/deviceWidthHelpers'
import { useReselector, useScrollRef, useWindowSize } from '@/utils/sharedHooks'
import {
  filterUserTransactions,
  getAllTransactionFilters,
} from '../transactions.selectors'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import {
  TRANSACTIONS_SORT_BY,
  updateTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import {
  restoreManualTransaction,
  updateUserTransaction,
} from '../transactions.slice'
import { Alert, Button, Table, TableHeaderSort, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import { UserTransactionsFilter } from '../UserTransactionsFilter'
import { getCurrentUser } from '@/selectors/user.selectors'

import { MobileTableRow } from './MobileTableRow'
import { TRANSACTIONS_TABLE_DESCRIPTION } from '../copyConstants'
import { MessageBanner } from './MessageBanner'
import {
  UndoTransactionContext,
  UNDO_TRANSACTION_TYPES,
} from '@/features/transactions/UndoTransactionContext'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import TransactionPagination from '@/features/financial-insights/transactions-panel/TransactionPagination'

// TODO: Move to v2  skipcq: JS-0099
import { Loader } from '@/src/components/BaseComponents'

interface TransactionTableProps {
  transactionsType?: 'expenses' | 'income'
  tableType: keyof typeof TRANSACTIONS_TABLE_DESCRIPTION
  fetching: boolean
  /* Indicates if we should default to showing editable fields */
  review?: boolean
  /*Indicates if we should allow ability to filter transactions */
  filterable: boolean
}

const TransactionsTable = ({
  transactionsType,
  tableType,
  fetching,
  review,
  filterable,
}: TransactionTableProps) => {
  const { scrollRef, scrollToRef } = useScrollRef()
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const [submitting, setSubmitting] = useState(false)
  const [error, showError] = useState(false)
  const [undoRevertError, setUndoRevertError] = useState(false)
  const [undoMessage, setUndoMessage] = useState('')
  const [searchParams] = useSearchParams()
  const currentUser = useReselector(getCurrentUser)
  const { transactions, count } = useReselector(
    filterUserTransactions,
    tableType
  )
  const { sortVal, sortAsc } = useReselector(getAllTransactionFilters)
  const markComplete = searchParams.get('markComplete')
  const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const { height } = useWindowSize()

  const onboardingStepCompletion = useCompleteOnboardingStepIfNeeded(
    GettingStartedOnboardingStep.clarifyUncategorizedTransactions
  )

  const {
    setUndoTransactionId,
    setUndoTransactionType,
    undoTransactionId,
    undoTransactionType,
  } = useContext(UndoTransactionContext)

  useEffect(() => {
    if (markComplete) {
      markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.reviewRecentTransactions,
        (event, properties) => track(event, properties)
      )
    }
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch, markComplete, track])

  const getSorted = (name: string) => {
    return name === sortVal
      ? sortAsc
        ? TableHeaderSort.ASCENDING
        : TableHeaderSort.DESCENDING
      : undefined
  }

  const sortColumn = useCallback(
    (name: string) => {
      if (sortVal === name) {
        dispatch(updateTransactionFilters({ sortAsc: !sortAsc }))
      } else {
        dispatch(updateTransactionFilters({ sortVal: name, sortAsc: false }))
      }
    },
    [dispatch, sortAsc, sortVal]
  )

  const submitReview = async () => {
    setSubmitting(true)

    try {
      await Promise.all(
        transactions.map((transaction) =>
          updateUserTransaction(transaction.id, {
            requestedClarificationAt: null,
            notifiedRequestedClarificationAt: null,
          })(dispatch)
        )
      )
      markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.transactionsNeedReview,
        (event, properties) => track(event, properties)
      )
      // Mark onboarding task as complete if needed
      onboardingStepCompletion.markComplete()
      setSubmitting(false)
    } catch (_) {
      showError(true)
    }
  }

  const handleUndo = async () => {
    try {
      const isTypeDelete = undoTransactionType === UNDO_TRANSACTION_TYPES.DELETE

      let txn
      if (isTypeDelete) {
        txn = await restoreManualTransaction(undoTransactionId)(dispatch)
      } else {
        txn = await updateUserTransaction(undoTransactionId.toString(), {
          manuallyDedupedAt: null,
        })(dispatch)
      }

      if (!txn) {
        setUndoRevertError(true)
        return
      }

      setUndoTransactionId(NaN)
      setUndoTransactionType('')
      setUndoRevertError(false)
      setUndoMessage('')
    } catch (err) {
      setUndoRevertError(true)
      console.error('Error undoing transaction', err)
    }
  }

  useEffect(() => {
    if (undoTransactionType !== '') {
      const errorMessage = 'Could not undo due to an error. Please try again.'
      const isTypeDelete = undoTransactionType === UNDO_TRANSACTION_TYPES.DELETE
      const message = isTypeDelete
        ? 'Transaction Deleted.'
        : 'Duplicate transaction has been removed.'

      setUndoMessage(undoRevertError ? errorMessage : message)
    }
  }, [undoRevertError, undoTransactionType])

  return (
    <div
      id="transactionsTableContainer"
      className={`
        flex flex-col w-full
        ${!isMobileOrTablet ? 'min-h-[500px]' : ''}
        ${!isMobileOrTablet && height >= 1000 ? 'h-[700px]' : ''}
        ${!isMobileOrTablet && height >= 800 && height < 1000 ? 'h-[600px]' : ''}
        ${!isMobileOrTablet && height >= 600 && height < 800 ? 'h-[500px]' : ''}
        ${!isMobileOrTablet && height < 600 ? 'h-[400px]' : ''}
      `}
    >
      <span ref={scrollRef} />
      <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6">
        {error && (
          <Alert
            variant="error"
            description="It looks like we were unable to save your review! Please refresh and try again."
          />
        )}

        {tableType !== 'all' && !isMobile && (
          <Alert
            variant="default"
            className="w-full text-center"
            description={TRANSACTIONS_TABLE_DESCRIPTION[tableType]}
          />
        )}
        {filterable && (
          <UserTransactionsFilter transactionsType={transactionsType} />
        )}

        {count === 0 && (
          <Text variant={TypographyStyles.H3} className="w-full text-center">
            No transactions yet!
          </Text>
        )}
      </div>

      {Boolean(undoTransactionId) && (
        <MessageBanner
          close={() => {
            setUndoTransactionId(NaN)
          }}
          error={undoRevertError}
          message={undoMessage}
          undo={handleUndo}
        />
      )}

      {count !== 0 && (
        <div className="overflow-visible">
          {/* Mobile & Tablet View */}
          <div className="block lg:hidden space-y-4">
            {transactions.map((transaction) => (
              <MobileTableRow
                key={transaction.id}
                transaction={transaction}
                review={review}
              />
            ))}

            {tableType === 'review' && (
              <div className="w-full">
                <Button onClick={submitReview} disabled={submitting}>
                  Complete review for {transactions.length} transactions
                </Button>
              </div>
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block">
            <Table className="w-full">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.DATE)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.DATE)}
                  >
                    Date
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    className="min-w-[274px]"
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.DESCRIPTION)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.DESCRIPTION)}
                  >
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.AMOUNT)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.AMOUNT)}
                  >
                    Amount
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.TYPE)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.TYPE)}
                  >
                    Type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.CATEGORY)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.CATEGORY)}
                  >
                    Category
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    className="min-w-[140px]"
                    sorted={getSorted(TRANSACTIONS_SORT_BY.ACCOUNT)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.ACCOUNT)}
                  >
                    Account
                  </Table.HeaderCell>
                  <Table.HeaderCell />
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {!fetching &&
                  currentUser &&
                  transactions.map((transaction) => (
                    <TransactionsTableRow
                      transaction={transaction}
                      key={transaction.id}
                      review={review}
                    />
                  ))}
                {fetching && (
                  <Table.Row>
                    <Loader />
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {tableType === 'review' && (
              <div className="mt-4 w-full text-right ml-auto ">
                <Button onClick={submitReview} disabled={submitting}>
                  Complete review for {transactions.length} transactions
                </Button>
              </div>
            )}
          </div>

          {/* Pagination (Shared) */}
          <div className="mt-6 flex justify-center lg:mt-8 mb-4">
            <TransactionPagination
              transactionsCount={count}
              scrollToRef={scrollToRef}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TransactionsTable
